import type { StepTimings } from '@/lib/assistant-cloud-local/types';

export const DB_NAME = 'assistant-cloud-local';
export const DB_VERSION = 2;

export const STORES = {
  THREADS: 'threads',
  MESSAGES: 'messages',
  ATTACHMENTS: 'attachments',
  PENDING_DELETIONS: 'pending_deletions',
} as const;

export interface DBSchema {
  threads: {
    key: string;
    value: {
      id: string;
      external_id?: string | null;
      title: string;
      is_archived: boolean;
      created_at: Date;
      updated_at: Date;
      last_message_at: Date;
    };
    indexes: {
      'by-archived': boolean;
      'by-created': Date;
      'by-updated': Date;
      'by-external-id': string;
    };
  };
  messages: {
    key: string;
    value: {
      id: string;
      remote_id?: string | null;
      thread_id: string;
      parent_id: string | null;
      height: number;
      format: string;
      content: unknown;
      created_at: Date;
      updated_at: Date;
      // Activity-trail timings sidecar — see the StoredMessage docblock in
      // ../../types.ts for the migration plan when IndexedDB is replaced.
      step_timings?: StepTimings;
      stream_duration_ms?: number;
    };
    indexes: {
      'by-thread': string;
      'by-parent': string;
      'by-format': string;
      'by-created': Date;
      'by-updated': Date;
      'by-thread-created': [string, Date];
      'by-thread-height': [string, number];
    };
  };
  attachments: {
    key: string;
    value: {
      id: string;
      name: string;
      content_type: string;
      size: number;
      data: Blob;
      created_at: Date;
    };
    indexes: {
      'by-created': Date;
    };
  };
  pending_deletions: {
    key: string;
    value: {
      message_id: string;
      remote_id: string;
      thread_id: string;
      queued_at: Date;
    };
    indexes: {
      'by-thread': string;
    };
  };
}

export function createSchema(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(STORES.THREADS)) {
    const threadsStore = db.createObjectStore(STORES.THREADS, { keyPath: 'id' });
    threadsStore.createIndex('by-archived', 'is_archived', { unique: false });
    threadsStore.createIndex('by-created', 'created_at', { unique: false });
    threadsStore.createIndex('by-updated', 'updated_at', { unique: false });
    threadsStore.createIndex('by-external-id', 'external_id', { unique: false });
  }

  if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
    const messagesStore = db.createObjectStore(STORES.MESSAGES, { keyPath: 'id' });
    messagesStore.createIndex('by-thread', 'thread_id', { unique: false });
    messagesStore.createIndex('by-parent', 'parent_id', { unique: false });
    messagesStore.createIndex('by-format', 'format', { unique: false });
    messagesStore.createIndex('by-created', 'created_at', { unique: false });
    messagesStore.createIndex('by-updated', 'updated_at', { unique: false });
    messagesStore.createIndex('by-thread-created', ['thread_id', 'created_at'], {
      unique: false,
    });
    messagesStore.createIndex('by-thread-height', ['thread_id', 'height'], {
      unique: false,
    });
  }

  if (!db.objectStoreNames.contains(STORES.ATTACHMENTS)) {
    const attachmentsStore = db.createObjectStore(STORES.ATTACHMENTS, {
      keyPath: 'id',
    });
    attachmentsStore.createIndex('by-created', 'created_at', { unique: false });
  }

  if (!db.objectStoreNames.contains(STORES.PENDING_DELETIONS)) {
    const pendingDeletionsStore = db.createObjectStore(STORES.PENDING_DELETIONS, {
      keyPath: 'message_id',
    });
    pendingDeletionsStore.createIndex('by-thread', 'thread_id', { unique: false });
  }
}
